import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Trash2, Plus, X, Check } from 'lucide-react';
import { CompanyNode } from '../types';
import {
  PendingEdit,
  EditFieldPath,
  ValidationError,
  PendingAddValue,
} from '../utils/pendingEdits.types';
import { isFieldStructural, applyPendingEdits } from '../utils/pendingEdits';
import './EntityTable.css';

interface EntityTableProps {
  companyNodes: CompanyNode[];
  onApply: (newNodes: CompanyNode[], hasStructural: boolean) => void;
  onClose: () => void;
}

interface EditableCellProps {
  value: any;
  pending: boolean;
  errorMsg?: string;
  onChange: (v: string) => void;
}

const EditableCell: React.FC<EditableCellProps> = React.memo(
  ({ value, pending, errorMsg, onChange }) => {
    return (
      <td
        className={`etp-cell ${pending ? 'pending' : ''} ${errorMsg ? 'error' : ''}`}
        title={errorMsg}
      >
        <input
          className="etp-cell-input"
          defaultValue={value === undefined || value === null ? '' : String(value)}
          key={String(value)}
          onBlur={(e) => {
            if (e.target.value !== String(value ?? '')) {
              onChange(e.target.value);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              (e.target as HTMLInputElement).value = String(value ?? '');
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
      </td>
    );
  }
);
EditableCell.displayName = 'EditableCell';

const PANEL_WIDTH_KEY = 'hvg-entity-table-width';
const PANEL_WIDTH_DEFAULT = 600;
const PANEL_WIDTH_MIN = 320;
const PANEL_WIDTH_MAX = 1200;

const EntityTable: React.FC<EntityTableProps> = ({ companyNodes, onApply, onClose }) => {
  const [pending, setPending] = useState<PendingEdit[]>([]);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [pendingAddCounter, setPendingAddCounter] = useState<number>(0);

  // Panel width — persist tussen sessies, sleepbaar via handle aan linker rand
  const [width, setWidth] = useState<number>(() => {
    const stored = localStorage.getItem(PANEL_WIDTH_KEY);
    const n = stored ? parseInt(stored, 10) : PANEL_WIDTH_DEFAULT;
    if (Number.isNaN(n)) return PANEL_WIDTH_DEFAULT;
    return Math.max(PANEL_WIDTH_MIN, Math.min(PANEL_WIDTH_MAX, n));
  });
  useEffect(() => {
    localStorage.setItem(PANEL_WIDTH_KEY, String(width));
  }, [width]);

  // Drag-resize state via refs (geen rerender per mousemove)
  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const rafRef = useRef<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragStateRef.current = { startX: e.clientX, startWidth: width };
    setIsDragging(true);
  }, [width]);

  useEffect(() => {
    if (!isDragging) return;
    const onMouseMove = (e: MouseEvent) => {
      const ds = dragStateRef.current;
      if (!ds) return;
      // Sleep naar links → breder (linker rand panel = right edge of viewport - width)
      const delta = ds.startX - e.clientX;
      const next = Math.max(PANEL_WIDTH_MIN, Math.min(PANEL_WIDTH_MAX, ds.startWidth + delta));
      // Throttle via requestAnimationFrame: voorkomt ResizeObserver-loop in
      // React Flow door rapid setState bij elke pixel.
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setWidth(next);
        rafRef.current = null;
      });
    };
    const onMouseUp = () => {
      dragStateRef.current = null;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      setIsDragging(false);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    // Voorkom text-selection tijdens slepen
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging]);

  const realNodes = useMemo(
    () =>
      companyNodes.filter(
        (n) => n.country !== 'EXT' && !n.id.startsWith('ext-')
      ),
    [companyNodes]
  );

  const displayRows = useMemo<CompanyNode[]>(() => {
    const addRows: CompanyNode[] = pending
      .filter((p) => p.type === 'add')
      .map((p) => {
        const v = (p.value || {}) as PendingAddValue;
        return {
          id: p.nodeId,
          parentId: null,
          name: v.name || '',
          country: v.country || '',
          directors: v.directors || [],
          shareholders: Array.isArray(v.shareholders) ? v.shareholders : [],
          ownershipInfo: '',
          position: v.country || '',
          depth: 0,
        };
      });
    return [...realNodes, ...addRows];
  }, [realNodes, pending]);

  // Max gevulde shareholder-index (inclusief pending edits zodat een nieuwe
  // shareholder in de "spare" laatste kolom direct een extra kolom triggert).
  const maxFilledShareholderIdx = useMemo(() => {
    let max = -1;
    realNodes.forEach((n) => {
      const len = n.shareholders?.length || 0;
      if (len - 1 > max) max = len - 1;
    });
    pending.forEach((p) => {
      if (p.type === 'edit' && p.field) {
        const m = p.field.match(/^shareholders\.(\d+)\.(name|percentage)$/);
        if (m) {
          const idx = parseInt(m[1], 10);
          // Alleen als de pending value niet leeg is, telt hij als "gevuld"
          const val = p.value;
          const filled = val !== undefined && val !== null && String(val).trim() !== '';
          if (filled && idx > max) max = idx;
        }
      }
      if (p.type === 'add' && p.value?.shareholders) {
        const len = p.value.shareholders.length;
        if (len - 1 > max) max = len - 1;
      }
    });
    return max;
  }, [realNodes, pending]);

  // Eén extra kolom achter de laatste gevulde, zodat user direct kan typen
  // voor een nieuwe shareholder. Minimum 2 kolommen tonen ook in lege tabel.
  const maxShareholders = Math.max(2, maxFilledShareholderIdx + 2);

  const SHAREHOLDER_FIELD_RE = /^shareholders\.(\d+)\.(name|percentage)$/;

  function getCellValue(node: CompanyNode, field: EditFieldPath): any {
    const p = pending.find(
      (pe) => pe.type === 'edit' && pe.nodeId === node.id && pe.field === field
    );
    if (p) return p.value;
    if (field === 'name') return node.name;
    if (field === 'country') return node.country;
    if (field === 'directors') return (node.directors || []).join('; ');
    const m = SHAREHOLDER_FIELD_RE.exec(field);
    if (m) {
      const idx = parseInt(m[1], 10);
      const sub = m[2] as 'name' | 'percentage';
      const sh = node.shareholders?.[idx];
      if (!sh) return '';
      return sub === 'name' ? sh.name : sh.percentage;
    }
    return '';
  }

  function setCellValue(nodeId: string, field: EditFieldPath, value: any): void {
    setPending((prev) => {
      // For pending-add rows, update the add edit's value directly
      if (nodeId.startsWith('pending-')) {
        return prev.map((p) => {
          if (p.type === 'add' && p.nodeId === nodeId) {
            const v = (p.value || {}) as PendingAddValue;
            const newV: PendingAddValue = {
              country: v.country || '',
              name: v.name || '',
              directors: v.directors || [],
              shareholders: Array.isArray(v.shareholders) ? [...v.shareholders] : [],
            };
            if (field === 'name') newV.name = String(value ?? '');
            else if (field === 'country') newV.country = String(value ?? '');
            else if (field === 'directors') {
              const s = String(value ?? '');
              newV.directors = s
                .split(/[;,]/)
                .map((d) => d.trim())
                .filter((d) => d.length > 0);
            } else {
              const m = SHAREHOLDER_FIELD_RE.exec(field);
              if (m) {
                const idx = parseInt(m[1], 10);
                const sub = m[2] as 'name' | 'percentage';
                while (newV.shareholders.length <= idx) {
                  newV.shareholders.push({ name: '', percentage: 0, isExternal: false });
                }
                newV.shareholders[idx] = { ...newV.shareholders[idx] };
                if (sub === 'name') {
                  newV.shareholders[idx].name = String(value ?? '');
                } else {
                  const num =
                    value === '' || value === null || value === undefined
                      ? 0
                      : Number(value);
                  newV.shareholders[idx].percentage = isNaN(num) ? 0 : num;
                }
              }
            }
            return { ...p, value: newV };
          }
          return p;
        });
      }
      const filtered = prev.filter(
        (p) => !(p.type === 'edit' && p.nodeId === nodeId && p.field === field)
      );
      filtered.push({
        type: 'edit',
        nodeId,
        field,
        value,
        isStructural: isFieldStructural(field),
      });
      return filtered;
    });
  }

  function isPending(nodeId: string, field: EditFieldPath): boolean {
    if (nodeId.startsWith('pending-')) {
      // The whole row is pending; flag every cell as pending
      return pending.some((p) => p.type === 'add' && p.nodeId === nodeId);
    }
    return pending.some(
      (p) => p.type === 'edit' && p.nodeId === nodeId && p.field === field
    );
  }

  function fieldError(nodeId: string, field: EditFieldPath): string | undefined {
    const found = errors.find((e) => e.nodeId === nodeId && e.field === field);
    return found?.message;
  }

  function isPendingDelete(nodeId: string): boolean {
    return pending.some((p) => p.type === 'delete' && p.nodeId === nodeId);
  }

  function handleAddRow(): void {
    const newId = `pending-${pendingAddCounter}`;
    setPendingAddCounter((c) => c + 1);
    setPending((prev) => [
      ...prev,
      {
        type: 'add',
        nodeId: newId,
        isStructural: true,
        value: { country: '', name: '', shareholders: [] } as PendingAddValue,
      },
    ]);
  }

  function handleDeleteRow(nodeId: string): void {
    if (nodeId.startsWith('pending-')) {
      setPending((prev) =>
        prev.filter((p) => !(p.type === 'add' && p.nodeId === nodeId))
      );
      return;
    }
    setPending((prev) => {
      const has = prev.some((p) => p.type === 'delete' && p.nodeId === nodeId);
      if (has) {
        return prev.filter((p) => !(p.type === 'delete' && p.nodeId === nodeId));
      }
      return [
        ...prev,
        { type: 'delete', nodeId, isStructural: true },
      ];
    });
  }

  function handleApply(): void {
    const result = applyPendingEdits(companyNodes, pending);
    if (result.errors.some((e) => e.level === 'error')) {
      setErrors(result.errors);
      return;
    }
    onApply(result.nodes, result.hasStructuralChanges);
    setPending([]);
    setErrors([]);
  }

  function handleDiscard(): void {
    setPending([]);
    setErrors([]);
  }

  return (
    <aside
      className="entity-table-panel"
      style={{ width: `${width}px`, flex: `0 0 ${width}px` }}
    >
      <div
        className={`etp-resize-handle ${isDragging ? 'dragging' : ''}`}
        onMouseDown={handleResizeStart}
        title="Sleep om breedte aan te passen"
      />
      <header className="etp-header">
        <h2>Entiteiten ({realNodes.length})</h2>
        <button className="etp-close" onClick={onClose} title="Sluiten">
          <X size={18} />
        </button>
      </header>

      <div className="etp-status-bar">
        <span
          className={`etp-pending-count ${pending.length > 0 ? 'has-pending' : ''}`}
        >
          {pending.length} {pending.length === 1 ? 'wijziging' : 'wijzigingen'}
        </span>
        <div className="etp-actions">
          <button
            className="etp-btn"
            onClick={handleDiscard}
            disabled={pending.length === 0}
          >
            Discard
          </button>
          <button
            className="etp-btn primary"
            onClick={handleApply}
            disabled={pending.length === 0}
          >
            <Check size={14} /> Toepassen
          </button>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="etp-errors">
          <strong>{errors.filter((e) => e.level === 'error').length} fout(en):</strong>
          <ul>
            {errors.slice(0, 5).map((e, i) => (
              <li key={i}>{e.message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="etp-table-wrap">
        <table className="etp-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Land</th>
              <th>Bedrijf</th>
              <th>Bestuurders</th>
              {Array.from({ length: maxShareholders }, (_, i) => (
                <React.Fragment key={i}>
                  <th>Sh {i + 1}</th>
                  <th>%{i + 1}</th>
                </React.Fragment>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              // Pre-compute set van namen die ergens als shareholder worden
              // gebruikt — voor isolatie-detectie. O(N×M) eenmalig per render
              // ipv per row.
              const usedAsShareholderName = new Set<string>();
              displayRows.forEach((row) => {
                for (let i = 0; i < maxShareholders; i++) {
                  const sh = String(
                    getCellValue(row, `shareholders.${i}.name` as EditFieldPath) || ''
                  ).trim();
                  if (sh) usedAsShareholderName.add(sh);
                }
              });
              return displayRows.map((node, idx) => {
              const isPendingDel = isPendingDelete(node.id);
              const isPendingAdd = node.id.startsWith('pending-');
              // Som van shareholder-percentages (gebruik current value incl pending)
              let pctSum = 0;
              let hasAnyShareholder = false;
              for (let i = 0; i < maxShareholders; i++) {
                const raw = getCellValue(
                  node,
                  `shareholders.${i}.percentage` as EditFieldPath
                );
                const num = typeof raw === 'number' ? raw : parseFloat(String(raw || ''));
                if (Number.isFinite(num)) pctSum += num;
                const shName = String(
                  getCellValue(node, `shareholders.${i}.name` as EditFieldPath) || ''
                ).trim();
                if (shName) hasAnyShareholder = true;
              }
              const isOverHundred = pctSum > 100.01;
              // Geïsoleerd = entiteit zonder ouder (geen shareholders) én
              // niet als shareholder bij anderen gebruikt
              const ownName = String(getCellValue(node, 'name') || '').trim();
              const isReferenced = ownName && usedAsShareholderName.has(ownName);
              const isIsolated = !!ownName && !hasAnyShareholder && !isReferenced;
              const tooltipParts: string[] = [];
              if (isOverHundred) tooltipParts.push(`Aandelen-totaal: ${pctSum.toFixed(2)}% (boven 100%)`);
              if (isIsolated) tooltipParts.push('Geïsoleerde entiteit (geen ouder, geen kinderen)');
              const classNames = [
                isPendingDel ? 'pending-delete' : '',
                isPendingAdd ? 'pending-add' : '',
                (isOverHundred || isIsolated) ? 'over-100-pct' : '',
              ].filter(Boolean).join(' ');
              return (
                <tr
                  key={node.id}
                  className={classNames}
                  title={tooltipParts.length > 0 ? tooltipParts.join(' · ') : undefined}
                >
                  <td className="row-num">{idx + 1}</td>
                  <EditableCell
                    value={getCellValue(node, 'country')}
                    pending={isPending(node.id, 'country')}
                    errorMsg={fieldError(node.id, 'country')}
                    onChange={(v) =>
                      setCellValue(node.id, 'country', v.toUpperCase().slice(0, 2))
                    }
                  />
                  <EditableCell
                    value={getCellValue(node, 'name')}
                    pending={isPending(node.id, 'name')}
                    errorMsg={fieldError(node.id, 'name')}
                    onChange={(v) => setCellValue(node.id, 'name', v)}
                  />
                  <EditableCell
                    value={getCellValue(node, 'directors')}
                    pending={isPending(node.id, 'directors')}
                    errorMsg={fieldError(node.id, 'directors')}
                    onChange={(v) => setCellValue(node.id, 'directors', v)}
                  />
                  {Array.from({ length: maxShareholders }, (_, i) => (
                    <React.Fragment key={i}>
                      <EditableCell
                        value={getCellValue(
                          node,
                          `shareholders.${i}.name` as EditFieldPath
                        )}
                        pending={isPending(
                          node.id,
                          `shareholders.${i}.name` as EditFieldPath
                        )}
                        errorMsg={fieldError(
                          node.id,
                          `shareholders.${i}.name` as EditFieldPath
                        )}
                        onChange={(v) =>
                          setCellValue(
                            node.id,
                            `shareholders.${i}.name` as EditFieldPath,
                            v
                          )
                        }
                      />
                      <EditableCell
                        value={getCellValue(
                          node,
                          `shareholders.${i}.percentage` as EditFieldPath
                        )}
                        pending={isPending(
                          node.id,
                          `shareholders.${i}.percentage` as EditFieldPath
                        )}
                        errorMsg={fieldError(
                          node.id,
                          `shareholders.${i}.percentage` as EditFieldPath
                        )}
                        onChange={(v) =>
                          setCellValue(
                            node.id,
                            `shareholders.${i}.percentage` as EditFieldPath,
                            v === '' ? '' : Number(v)
                          )
                        }
                      />
                    </React.Fragment>
                  ))}
                  <td className="etp-row-actions">
                    <button
                      onClick={() => handleDeleteRow(node.id)}
                      className={isPendingDel ? 'active' : ''}
                      title={
                        isPendingDel
                          ? 'Klik nogmaals om te annuleren'
                          : 'Verwijderen'
                      }
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
              });
            })()}
          </tbody>
        </table>
        <button className="etp-add-row" onClick={handleAddRow}>
          <Plus size={14} /> Nieuwe entiteit
        </button>
      </div>
    </aside>
  );
};

export default EntityTable;
